<template>
    <div>
        <div class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content || ''" />
                </e-field>
            </div>
        </div>
        <!-- eBook Display Boxes -->
        <div v-for="(ebook, index) in ebooks" :key="`ebook-${index}`" class="mb-6">
            <div class="tw-border tw-rounded-md tw-flex tw-flex-col tw-justify-between">
                <div class="is-flex mb-3">
                    <div class="tw-flex-shrink-0 tw-mr-4">
                        <figure class="ebook-cover-figure">
                            <img
                                :src="ebook?.ebook?.cover_url || '/placeholder-book-cover.jpg'"
                                alt="Book Cover"
                                class="ebook-cover-img"
                            />
                        </figure>
                    </div>
                    <div class="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-justify-center ml-2">
                        <p class="has-text-weight-bold mb-2">
                            <span class="has-text-weight-semibold">Title:</span>
                            {{ displayEbookHeading(ebook) }}
                        </p>
                        <p v-if="shouldShowLearnerDeepLinkSubtitle(ebook)" class="has-text-grey mb-2">
                            {{ ebook.deep_link_title }}
                        </p>
                        <p v-if="ebook?.ebook?.author" class="has-text-grey mb-2">
                            <span class="has-text-weight-semibold">Author:</span>
                            {{ ebook.ebook.author }}
                        </p>
                        <p v-if="ebook?.ebook?.publisher" class="has-text-grey mb-2">
                            <span class="has-text-weight-semibold">Publisher:</span>
                            {{ ebook.ebook.publisher }}
                        </p>
                        <p v-if="ebook?.ebook?.edition" class="has-text-grey mb-2">
                            <span class="has-text-weight-semibold">Edition:</span>
                            {{ ebook.ebook.edition }}
                        </p>
                        <p v-if="ebook?.ebook?.format" class="has-text-grey mb-2">
                            <span class="has-text-weight-semibold">Format:</span>
                            {{ ebook.ebook.format }}
                        </p>
                    </div>
                </div>
                <div class="buttons-container">
                    <div
                        v-if="isOptedOut(ebook)"
                        class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-whitespace-nowrap"
                    >
                        <e-icon icon="info-circle" size="is-small" type="is-info" />
                        <span>You declined this Ebook</span>
                    </div>

                    <e-button
                        :type="!isOptedOut(ebook) ? 'is-primary' : ''"
                        :class="{ 'opted-out-button-outlined': isOptedOut(ebook) }"
                        outlined
                        size="is-small"
                        icon-left="eye"
                        :disabled="isOptedOut(ebook)"
                        @click="!isOptedOut(ebook) && readFromBeginning(ebook)"
                    >
                        Read from the Beginning
                    </e-button>

                    <e-button
                        :type="!isOptedOut(ebook) ? 'is-primary' : ''"
                        :class="{ 'opted-out-button': isOptedOut(ebook) }"
                        size="is-small"
                        icon-left="eye"
                        :disabled="isOptedOut(ebook)"
                        @click="!isOptedOut(ebook) && continueReading(ebook)"
                    >
                        Continue Reading
                    </e-button>
                </div>
            </div>
        </div>

        <!-- Late Notice -->
        <div v-if="isLate && !completed && !userHasAuthorizedAttempt" class="columns">
            <div class="column">
                <late-notice :can-submit-late="canSubmitLate" />
            </div>
        </div>

        <!-- Attempt Authorization Notice -->
        <attempt-authorization-notice
            v-if="hasAnyAttemptAuthorizations"
            :attempt-authorizations="userUnusedAuthorizedAttempts"
        />

        <!-- ebook-preview-drawer (only mount when a valid resource id exists) -->
        <ebook-preview-drawer
            v-if="ltiResourceLinkId !== null && ltiResourceLinkId !== ''"
            :show-ebook-preview-drawer="showEbookPreviewDrawer"
            :selected-ebook-title="selectedEbookTitle"
            :selected-ebook-format="selectedEbookFormat"
            :lti-resource-link-id="ltiResourceLinkId"
            :has-specific-location="hasSpecificLocation"
            :continue-reading="isContinueReading"
            @close="showEbookPreviewDrawer = false"
        />

        <div v-if="userHasAuthorizedAttempt || (!completed && !readOnly)" class="columns mt-3">
            <div class="column">
                <e-button :loading="isLoading" :disabled="disabled" type="is-primary" @click="submit" @keyup="submit">
                    Mark As Complete
                </e-button>
            </div>
        </div>

        <div v-if="!readOnly && canViewSubmissions" class="columns mt-3">
            <div class="column">
                <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading" />
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <attachments :media="media" :namespace="namespace" :uuid="uuid" />
            </div>
        </div>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import LateNotice from '@/components/activity-types/course-activity-types/LateNotice.vue';
import AttemptAuthorizationNotice from '@/components/attempt-authorizations/AttemptAuthorizationNotice.vue';
import EbookPreviewDrawer from '@/components/ebook/EbookPreviewDrawer.vue';
import { shouldShowDistinctDeepLinkTitle } from '@/components/ebook/ebookDisplayHelpers.js';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';

export default {
    name: 'EBook',

    components: {
        LateNotice,
        AttemptAuthorizationNotice,
        EbookPreviewDrawer,
        DisplayContent,
        Submissions,
    },

    mixins: [IsLateMixin, CourseActivityMixin, AttemptAuthorizationMixin],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data() {
        return {
            showEbookPreviewDrawer: false,
            selectedEbookTitle: '',
            selectedEbookFormat: '',
            ltiResourceLinkId: null,
            optedOutEbooks: new Set(),
            hasSpecificLocation: false,
            isContinueReading: false,
        };
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        ebooks() {
            return this.activityContent.active_lti_ebooks ?? [];
        },

        canSubmit() {
            // Check for authorized attempts first - they should bypass restrictions
            if (this.userHasAuthorizedAttempt) {
                return true;
            }

            // Allow if not completed and not in read-only mode
            if (!this.completed && !this.readOnly) {
                // Check if late and late submissions are not allowed
                if (this.isLate && !this.canSubmitLate) {
                    return false;
                }
                return true;
            }

            return false;
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.course ? this.course.id : this.$route.params.courseId,
                activity_id: this.activityId,
                activity_type: this.get('activity_type'),
            };
        },
    },

    async mounted() {
        try {
            // get vitalsource related info
            await this.$store.dispatch('Integrations/getVitalSourceRelatedInfo');
        } catch {
            this.showEbookPreviewDrawer = false;
            this.ltiResourceLinkId = null;
            this.hasSpecificLocation = false;
            this.isContinueReading = false;
        }
    },

    methods: {
        displayEbookHeading(ebook) {
            const fromEbook = ebook?.ebook?.title;
            if (typeof fromEbook === 'string' && fromEbook.trim() !== '') {
                return fromEbook.trim();
            }
            const activityTitle = this.get('title');
            if (activityTitle != null && String(activityTitle).trim() !== '') {
                return String(activityTitle).trim();
            }
            return 'Unknown';
        },

        shouldShowLearnerDeepLinkSubtitle(ebook) {
            return shouldShowDistinctDeepLinkTitle(ebook);
        },

        isOptedOut(ebook) {
            // Check local opt-out set
            if (this.optedOutEbooks.has(ebook.id)) {
                return true;
            }
            // Check API data: if any enrollment has opted_out_at
            if (Array.isArray(ebook.enrollments)) {
                return ebook.enrollments.some(e => e.opted_out_at);
            }
            return false;
        },

        readFromBeginning(ebook) {
            if (!ebook?.lti_resource_link_id) {
                this.$edverumToast.open({ message: 'No Ebook URL available', type: 'is-info' });
                return;
            }

            this.$dialog.confirm({
                title: 'Read from the beginning',
                message:
                    'Please note that using this Ebook launch option will reset your last reading position in this Ebook.',
                confirmText: 'Continue',
                cancelText: 'Cancel',
                type: 'is-info',
                onConfirm: () => {
                    this.ltiResourceLinkId = ebook.lti_resource_link_id;
                    this.selectedEbookTitle = this.displayEbookHeading(ebook);
                    this.selectedEbookFormat = ebook?.ebook?.format || '';
                    this.hasSpecificLocation = ebook.has_specific_location || false;
                    this.isContinueReading = false;
                    this.showEbookPreviewDrawer = true;
                },
            });
        },

        continueReading(ebook) {
            if (!ebook?.lti_resource_link_id) {
                this.$edverumToast.open({ message: 'No Ebook URL available', type: 'is-info' });
                return;
            }

            this.ltiResourceLinkId = ebook.lti_resource_link_id;
            this.selectedEbookTitle = this.displayEbookHeading(ebook);
            this.selectedEbookFormat = ebook?.ebook?.format || '';
            this.hasSpecificLocation = ebook.has_specific_location || false;
            this.isContinueReading = true;
            this.showEbookPreviewDrawer = true;
        },

        // submit is provided by CourseActivityMixin — it uses `payload` computed above
    },
};
</script>

<style>
.buttons-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
}

.opted-out-button-outlined {
    background-color: transparent;
    color: #b5b5b5;
    border-color: #b5b5b5;
    cursor: not-allowed;
}

.opted-out-button-outlined:hover {
    background-color: transparent;
    color: #b5b5b5;
    border-color: #b5b5b5;
}

.opted-out-button {
    background-color: #b5b5b5;
    color: white;
    border-color: #b5b5b5;
    cursor: not-allowed;
}

.opted-out-button:hover {
    background-color: #b5b5b5;
    color: white;
    border-color: #b5b5b5;
}

/* eBook cover sizing helpers */
.ebook-cover-figure {
    width: 128px;
    height: 184px;
    overflow: hidden;
    margin: 0;
}

.ebook-cover-img {
    width: 128px;
    height: 184px;
    object-fit: cover;
    object-position: center;
    display: block;
    border-radius: 0.375rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
</style>
